import * as React from "react"
import * as SheetPrimitive from "@radix-ui/react-dialog"
import { cva, type VariantProps } from "class-variance-authority"
import { X } from "lucide-react"
import { cn } from "@shared/lib/utils"
import { Z_INDEX_CLASSES } from "@shared/lib/z-index"

/**
 * Sheet component root - provides context for all sheet-related components
 */
const Sheet = SheetPrimitive.Root

/**
 * Overlay backdrop for the sheet - provides darkened background when sheet is open
 */
const SheetOverlay = React.forwardRef<
  React.ElementRef<typeof SheetPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof SheetPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <SheetPrimitive.Overlay
    ref={ref}
    className={cn(
      "fixed inset-0",
      Z_INDEX_CLASSES.SHEET,
      "bg-foreground opacity-80 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
      className
    )}
    {...props}
  />
))
SheetOverlay.displayName = SheetPrimitive.Overlay.displayName

const sheetVariants = cva(
  [
    "fixed bg-surface pt-6 px-8 pb-8 shadow-lg transition ease-in-out",
    "data-[state=open]:animate-in data-[state=closed]:animate-out",
    "data-[state=closed]:duration-300 data-[state=open]:duration-500",
    Z_INDEX_CLASSES.SHEET,
  ],
  {
    variants: {
      side: {
        top: [
          "inset-x-4 top-4 rounded-[32px]",
          "data-[state=closed]:slide-out-to-top data-[state=open]:slide-in-from-top",
        ],
        bottom: [
          "inset-x-4 bottom-4 rounded-[32px]",
          "data-[state=closed]:slide-out-to-bottom data-[state=open]:slide-in-from-bottom",
        ],
        left: [
          "top-4 bottom-4 left-4 w-3/4 rounded-[32px]",
          "data-[state=closed]:slide-out-to-left data-[state=open]:slide-in-from-left",
        ],
        right: [
          "inset-y-4 right-0 sm:right-4 w-full",
          "rounded-l-[32px] sm:rounded-[32px]",
          "border-l border-[#F4F6F6]",
          "data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right",
        ],
      },
      /**
       * Controls the maximum width of the sheet panel.
       * Primarily applies to left/right side sheets.
       *
       * sm  → 384px  – simple action sheets (confirmations, quick forms)
       * md  → 512px  – standard detail / creation sheets          (default)
       * lg  → 672px  – complex forms, wider content
       * xl  → 768px  – multi-column panels, rich detail views
       * 2xl → 75vw   – wide panels, split-view layouts
       */
      size: {
        sm: "sm:max-w-sm",
        md: "sm:max-w-lg",
        lg: "sm:max-w-2xl",
        xl: "sm:max-w-3xl",
        "2xl": "sm:max-w-[1440px]",
      },
    },
    defaultVariants: {
      side: "right",
      size: "md",
    },
  }
)

interface SheetContentProps
  extends React.ComponentPropsWithoutRef<typeof SheetPrimitive.Content>,
    VariantProps<typeof sheetVariants> {}

/**
 * Main sheet content container with built-in overlay and close button
 * @param side - Which side of the screen the sheet should slide from (default: "right")
 * @param size - Maximum width of the panel: "sm" (384px), "md" (512px, default), "lg" (672px), "xl" (768px)
 */
const SheetContent = React.forwardRef<
  React.ElementRef<typeof SheetPrimitive.Content>,
  SheetContentProps
>(({ side = "right", size = "md", className, children, ...props }, ref) => (
  <SheetPrimitive.Portal>
    <SheetOverlay />
    <SheetPrimitive.Content
      ref={ref}
      className={cn(sheetVariants({ side, size }), className)}
      {...props}
    >
      {children}
      <SheetPrimitive.Close className="absolute right-8 top-8 rounded-sm opacity-70 ring-offset-background transition-all hover:opacity-100 hover:text-destructive focus:outline-none disabled:pointer-events-none">
        <X className="h-6 w-6" />
      </SheetPrimitive.Close>
    </SheetPrimitive.Content>
  </SheetPrimitive.Portal>
))
SheetContent.displayName = SheetPrimitive.Content.displayName


/**
 * Frame component for sheet content - provides white background with rounded corners and shadow
 */
const SheetFrame = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "flex flex-col bg-card rounded-xl shadow-[0px_10px_28px_0px_rgba(11,59,69,0.1),0px_1px_2px_0px_rgba(11,59,69,0.06)] overflow-hidden",
      // Hide scrollbars while keeping scroll functionality
      "[&_*]:scrollbar-hidden",
      className
    )}
    {...props}
  />
)
SheetFrame.displayName = "SheetFrame"

/**
 * Header section for the sheet - groups title and description
 */
const SheetHeader = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn("flex flex-col space-y-2", className)}
    {...props}
  />
)
SheetHeader.displayName = "SheetHeader"

/**
 * Accessible title component for the sheet
 */
const SheetTitle = React.forwardRef<
  React.ElementRef<typeof SheetPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof SheetPrimitive.Title>
>(({ className, ...props }, ref) => (
  <SheetPrimitive.Title
    ref={ref}
    className={cn("text-2xl font-medium leading-8", className)}
    {...props}
  />
))
SheetTitle.displayName = SheetPrimitive.Title.displayName

/**
 * Optional description component for the sheet - used for accessibility
 */
const SheetDescription = React.forwardRef<
  React.ElementRef<typeof SheetPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof SheetPrimitive.Description>
>(({ className, ...props }, ref) => (
  <SheetPrimitive.Description
    ref={ref}
    className={cn("text-sm text-muted-foreground leading-5 tracking-[-0.01em]", className)}
    {...props}
  />
))
SheetDescription.displayName = SheetPrimitive.Description.displayName

export {
  Sheet,
  SheetContent,
  SheetFrame,
  SheetHeader,
  SheetTitle,
  SheetDescription,
}
